import React, { useEffect, useState, useRef } from 'react';
import Table from '../../components/common/table/Table';
import { FiDownload, FiMoreHorizontal, FiCopy, FiShare2, FiClipboard } from 'react-icons/fi';
import { FaTrash } from 'react-icons/fa';
import { MdEdit } from 'react-icons/md';
import { useBts } from '../../context/btsContext/BtsContextProvider';
import { useDebouncedCallback } from '@/utils/useDebouncedCallback';
import Step2Report from '@/pages/bts/Step2Report';
import ShareQuoteModal from '@/pages/bts/ShareQuoteModal';
import { useNavigate } from 'react-router-dom';
import { getClassListForProposal, updateSchoolActiveStatus } from '@/services/BtsService';

// ... existing code ...


import { showAlert } from '@/components';
import {
  ModalProvider,
  useModal,
} from '@/components/common/modal/ModalContext';
import SearchInput from '../../components/common/inputs/SearchInput';

// Simple Dropdown Component
const Dropdown = ({ trigger, children, align = 'right' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropUp, setDropUp] = useState(false);
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - rect.bottom;
      const dropdownHeight = 250; // Approximate height of dropdown menu

      // If not enough space below, show dropdown above
      setDropUp(spaceBelow < dropdownHeight);
    }
  }, [isOpen]);

  return (
    <div className="relative inline-block text-left" ref={dropdownRef}>
      <div ref={triggerRef} onClick={() => setIsOpen(!isOpen)}>
        {trigger}
      </div>
      {isOpen && (
        <div className={`absolute ${align === 'right' ? 'right-0' : 'left-0'} z-50 ${dropUp ? 'bottom-full mb-2' : 'mt-2'} w-56 ${dropUp ? 'origin-bottom-right' : 'origin-top-right'} rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none`}>
          <div className="py-1" onClick={() => setIsOpen(false)}>
            {children}
          </div>
        </div>
      )}
    </div>
  );
};

const BtsTable = () => {
  const {
    getAllBtsProposals,
    btsTableData,
    totalElements,
    currentPage,
    setCurrentPage,
    pageSize,
    setSortBy,
    setSortDir,
    sortBy,
    sortDir,
    searchQuery,
    setSearchQuery,
    setBtsSchool,
    deleteSchoolProposal,
    exportBTSExcelReport,
    exportBtsShReportAPI,
    kitImportTemplateReportAPI,
    exportContractExportReportAPI,
    setBtsTableData,
  } = useBts();
  const { openModal, closeModal } = useModal();

  const navigate = useNavigate();
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    getAllBtsProposals();
    // eslint-disable-next-line
  }, [currentPage, searchQuery, sortBy, sortDir]);


  const handleStep2Report = async (proposalId, schoolName) => {
    try {
      const response = await getClassListForProposal(proposalId);
      if (!response || response.length === 0) {
        showAlert('error', 'Products for this class or year does not exist');
        return;
      }
      openModal(
        <Step2Report
          proposalId={proposalId}
          data={response}
          schoolName={schoolName}
          onClose={closeModal}
        />,
      );
    } catch (error) {
      console.error('Error in fetching step 2 report data');
    }
  };

  const handleSortChange = ({ field, direction }) => {
    setSortBy(field);
    setSortDir(direction);
  };

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setInputValue(val);
    debouncedSetSearchQuery(val);
  };

  const { call: debouncedSetSearchQuery } = useDebouncedCallback((val) => {
    setSearchQuery(val);
    setCurrentPage(0);
  }, 300);

  const handleEdit = (row) => {
    setBtsSchool((prev) => ({
      ...prev,
      proposal: { ...row },
    }));
    navigate('/bts/create-proposal');
  };

  const handleDelete = async (row) => {
    await deleteSchoolProposal(row.id);
  };

  const handleCopyQuoteId = async (quoteId) => {
    if (!quoteId) return;
    try {
      await navigator.clipboard.writeText(quoteId);
      showAlert('success', 'Quote ID copied');
    } catch (err) {
      showAlert('error', 'Failed to copy Quote ID');
    }
  };

  const handleShareQuoteId = async (row) => {
    if (!row.id) return;
    // Parse emails: prefer emailList array, fall back to splitting the email string
    const emails =
      Array.isArray(row.emailList) && row.emailList.length > 0
        ? row.emailList
        : row.email
          ? row.email.split(',').map((e) => e.trim()).filter(Boolean)
          : [];

    openModal(
      <ShareQuoteModal
        proposalId={row.id}
        emailList={emails}
        onSuccess={(updatedProposal) => {
          if (updatedProposal && updatedProposal.id) {
            const newData = btsTableData.map((item) =>
              item.id === updatedProposal.id ? updatedProposal : item,
            );
            setBtsTableData(newData);
          }
        }}
      />,
      'Share Quote ID',
    );
  };

  const handleToggleActiveStatus = async (row) => {
    if (!row.quoteId) return;
    try {
      // Robust check for current status
      const currentStatus = (row.schoolIsActive === true || row.schoolIsActive === 'true') ||
        (row.school_is_active === true || row.school_is_active === 'true');
      const newStatus = !currentStatus;
      await updateSchoolActiveStatus(row.quoteId, newStatus);
      getAllBtsProposals(); // Refresh table
    } catch (err) {
      console.error(err);
    }
  };

  const columns = [
    {
      header: 'Created Date',
      field: 'createdDate',
      render: (row) => {
        if (!row.createdDate) return '';
        return new Date(row.createdDate).toLocaleDateString();
      },
    },
    { header: 'School Name', field: 'schoolName' },
    {
      header: 'Email',
      field: 'email',
      render: (row) => {
        if (!row.email) return '-';
        // Show only the first email from the comma-separated list
        const firstEmail = row.email.split(',')[0].trim();
        return firstEmail || '-';
      },
    },
    { header: 'Business Manager', field: 'businessManager' },
    {
      header: 'Sales Rep',
      field: 'salesRep.firstName',
      render: (row) => row.salesRep?.firstName || 'Admin',
    },
    { header: 'Booklist type', field: 'booklistType' },
    {
      header: 'Date modified',
      field: 'updatedDate',
      render: (row) => {
        if (!row.updatedDate) return '-';
        return new Date(row.updatedDate).toLocaleDateString();
      },
    },
    {
      header: 'Status',
      field: 'status',
      render: (row) => {
        let statusLabel = '';
        let styleClass = '';

        // Priority 1: Pending Reviews (from API)
        if (row.hasPendingReviews) {
          statusLabel = 'Review Pending';
          styleClass = 'bg-orange-100 text-orange-800';
        } else {
          // Priority 2: Status Mapping
          const rawStatus = row.status || 'DRAFT';

          if (rawStatus === 'DRAFT') {
            statusLabel = 'Draft';
            styleClass = 'bg-gray-100 text-gray-800';
          } else if (rawStatus === 'QUOTING') {
            statusLabel = 'Submitted';
            styleClass = 'bg-green-100 text-green-800';
          } else if (rawStatus === 'QUOTED') {
            statusLabel = 'Quoted';
            styleClass = 'bg-green-100 text-green-800';
          } else if (rawStatus === 'CHANGES') {
            statusLabel = 'Review Pending';
            styleClass = 'bg-orange-100 text-orange-800';
          } else if (rawStatus === 'CONFIRMED') {
            statusLabel = 'Approved';
            styleClass = 'bg-green-100 text-green-800';
          } else {
            // Default formatting for other statuses
            statusLabel = rawStatus.charAt(0) + rawStatus.slice(1).toLowerCase().replace(/_/g, ' ');
            // Check if it maps to known styles based on the result label, otherwise default blue
            if (statusLabel === 'Completed') { // Assuming COMPLETED maps here if not caught above
              styleClass = 'bg-green-100 text-green-800';
            } else if (rawStatus === 'ACCEPTED' || rawStatus === 'CONVERTED') {
              styleClass = 'bg-blue-100 text-blue-800';
            } else if (rawStatus === 'EXPIRED' || rawStatus === 'DECLINED') {
              styleClass = 'bg-red-100 text-red-800';
            } else {
              styleClass = 'bg-blue-100 text-blue-800';
            }
          }
        }

        // Note: Old implementation defaulted to Blue for unknown. 
        // We will keep the refined colors for Accepted/Expired if possible, but user asked for "changed UI".
        // Let's stick strictly to what the user had if possible, but the old switch only had Draft, Submitted, Review Pending.
        // It defaulted to Blue for everything else.

        // Strict restoration of previous logic for colors:
        if (statusLabel === 'Draft') styleClass = 'bg-gray-100 text-gray-800';
        else if (statusLabel === 'Submitted') styleClass = 'bg-green-100 text-green-800';
        else if (statusLabel === 'Review Pending') styleClass = 'bg-orange-100 text-orange-800';
        else if (statusLabel === 'Completed' || statusLabel === 'Approved') styleClass = 'bg-green-100 text-green-800'; // Commonly expected
        // Fallback for others (Accepted, etc) -> Blue
        else if (!styleClass) styleClass = 'bg-blue-100 text-blue-800';

        return (
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${styleClass}`}>
            {statusLabel}
          </span>
        );
      },
    },
    {
      header: 'Download',
      render: (row) => (
        <Dropdown trigger={
          <button className="inline-flex justify-center w-full px-2 py-2 text-sm font-medium text-blue-600 hover:text-blue-500">
            <FiDownload size={20} />
          </button>
        }>
          <button onClick={() => exportBtsShReportAPI(row.id, row.schoolName)} className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100">Step 1 Report</button>
          <button onClick={() => handleStep2Report(row.id, row.schoolName)} className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100">Step 2 Report</button>
          <button onClick={() => kitImportTemplateReportAPI(row.id, row.schoolName)} className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100">Step 3 Report</button>
          <button onClick={() => exportContractExportReportAPI(row.id, row.schoolName)} className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100">Step 4 Report</button>
          <button onClick={() => exportBTSExcelReport(row.id)} className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100">BTS Proposal-Excel</button>
        </Dropdown>
      )
    },
    {
      header: 'Actions',
      render: (row) => {
        // Helper to robustly check active status
        const getSchoolActiveStatus = (row) => {
          if (row.schoolIsActive === true || row.schoolIsActive === 'true') return true;
          if (row.school_is_active === true || row.school_is_active === 'true') return true;
          return false;
        };

        const isSchoolActive = getSchoolActiveStatus(row);

        return (
          <div className="flex items-center space-x-4">
            <button onClick={() => handleDelete(row)} className="text-red-500 hover:text-red-700">
              <FaTrash size={16} />
            </button>
            <button onClick={() => handleEdit(row)} className="text-gray-500">
              <MdEdit size={16} />
            </button>

            <Dropdown trigger={
              <button className="inline-flex justify-center w-full text-sm font-medium text-gray-700 hover:text-gray-900">
                <FiMoreHorizontal size={20} />
              </button>
            }>
              <button
                onClick={() => handleCopyQuoteId(row.quoteId)}
                disabled={!row.quoteId}
                className={`block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 ${!row.quoteId ? 'opacity-50 cursor-not-allowed' : ''}`}>
                <FiCopy className="inline mr-2" /> Copy Quote ID
              </button>
              <button
                onClick={() => handleShareQuoteId(row)}
                disabled={!row.quoteId}
                className={`block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 ${!row.quoteId ? 'opacity-50 cursor-not-allowed' : ''}`}>
                <FiShare2 className="inline mr-2" /> Share Quote ID
              </button>
              <button
                onClick={() => navigate(`/bts/review-changes/${row.id}`)}
                disabled={row.status === 'DRAFT'}
                className={`block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 ${row.status === 'DRAFT' ? 'opacity-50 cursor-not-allowed' : ''}`}>
                <FiClipboard className="inline mr-2" /> Review changes
              </button>
              <button
                onClick={() => handleToggleActiveStatus(row)}
                disabled={!row.quoteId}
                className={`block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 ${!row.quoteId ? 'opacity-50 cursor-not-allowed' : ''}`}>
                <FiCopy className="inline mr-2" /> {isSchoolActive ? 'Disable Proposal' : 'Enable Proposal'}
              </button>
            </Dropdown>
          </div>
        );
      },
    },
  ];

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="mb-4 flex justify-end">
        <div className="relative w-64">
          <SearchInput
            placeholder="Search using ID, School, creator"
            value={inputValue}
            onChange={handleSearchChange}
            onSearchClick={() => {
              debouncedSetSearchQuery(inputValue);
            }}
            className="w-full"
          />
        </div>
      </div>
      <Table
        columns={columns}
        data={btsTableData}
        totalElements={totalElements}
        pageSize={pageSize}
        currentPage={currentPage}
        onPageChange={(newPage) => setCurrentPage(newPage)}
        onSortChange={handleSortChange}
        sortConfig={{ field: sortBy, direction: sortDir }}
      />
    </div>
  );
};

const WrappedBtsTable = () => (
  <ModalProvider>
    <BtsTable />
  </ModalProvider>
);

export default WrappedBtsTable;
